import type { Preferences } from '../../utils/types';
import {
  canAccessAccountDraft,
  getAccountDraftPrefix,
} from '../../auth/accountSession';
import { GAME_TO_API, isGameOption, OPTION_ORDER } from './options';
import type {
  PlanStudioDraft,
  StudioActivity,
  StudioOptionId,
  StudioSelection,
  StudioSubject,
  Translate,
} from './types';

export const DEFAULT_SUBJECT_DURATION = '10';

// Matches the PlanActivity.times limit in the API schema.
export const MAX_PLAN_ACTIVITY_TIMES = 100;

export const createId = (prefix: string): string =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const getDraftStorageKey = (targetId: string): string => {
  const prefix = getAccountDraftPrefix();
  return prefix ? `${prefix}${targetId}` : '';
};

const createSelectionRecord = (
  getValue: (optionId: StudioOptionId) => number
): StudioSelection =>
  OPTION_ORDER.reduce((record, optionId) => {
    record[optionId] = getValue(optionId);
    return record;
  }, {} as StudioSelection);

const isStudioOptionId = (value: unknown): value is StudioOptionId =>
  typeof value === 'string' && OPTION_ORDER.includes(value as StudioOptionId);

const normalizeStoredSelections = (
  selections: Partial<Record<StudioOptionId, unknown>>
): StudioSelection =>
  createSelectionRecord((optionId) => {
    const parsed = Number.parseInt(String(selections[optionId] ?? 0), 10);
    return Number.isFinite(parsed) && parsed > 0 ? 1 : 0;
  });

const normalizeStoredSubjects = (subjects: StudioSubject[]): StudioSubject[] =>
  subjects.map((subject) => ({
    ...subject,
    activities: subject.activities.filter(
      (activity) =>
        isStudioOptionId(activity.optionId) && isGameOption(activity.optionId)
    ),
  }));

export const readStoredDraft = (storageKey: string): PlanStudioDraft | null => {
  if (!canAccessAccountDraft(storageKey)) return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as PlanStudioDraft;
    if (!parsed.selections || !Array.isArray(parsed.subjects)) {
      return null;
    }

    return {
      ...parsed,
      selections: normalizeStoredSelections(parsed.selections),
      subjects: normalizeStoredSubjects(parsed.subjects),
    };
  } catch (error) {
    console.error('Failed to read Plan Studio draft:', error);
    return null;
  }
};

export const writeStoredDraft = (
  storageKey: string,
  draft: PlanStudioDraft
): boolean => {
  if (!canAccessAccountDraft(storageKey)) return false;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(draft));
    return true;
  } catch (error) {
    console.error('Failed to save Plan Studio draft:', error);
    return false;
  }
};

export const clearStoredDraft = (storageKey: string): boolean => {
  if (!canAccessAccountDraft(storageKey)) return false;
  try {
    window.localStorage.removeItem(storageKey);
    return true;
  } catch (error) {
    console.error('Failed to clear Plan Studio draft:', error);
    return false;
  }
};

export const createEmptySelections = (
  preferences: Preferences
): StudioSelection => {
  const preferredGames = new Set(preferences.preferredGames);

  return createSelectionRecord((optionId) => {
    if (optionId === 'generateImages') {
      return preferences.imageGeneration ? 1 : 0;
    }

    if (optionId === 'generateVideo') {
      return preferences.videoGeneration ? 1 : 0;
    }

    return isGameOption(optionId) && preferredGames.has(GAME_TO_API[optionId])
      ? 1
      : 0;
  });
};

export const getOptionLabels = (t: Translate): Record<StudioOptionId, string> =>
  OPTION_ORDER.reduce(
    (labels, optionId) => {
      labels[optionId] = t(`planAhead.studio.options.${optionId}.label`);
      return labels;
    },
    {} as Record<StudioOptionId, string>
  );

export const getOptionBadges = (t: Translate): Record<StudioOptionId, string> =>
  OPTION_ORDER.reduce(
    (badges, optionId) => {
      badges[optionId] = t(`planAhead.studio.options.${optionId}.badge`);
      return badges;
    },
    {} as Record<StudioOptionId, string>
  );

export const groupSubjectActivities = (
  activities: StudioActivity[]
): Array<{
  optionId: StudioOptionId;
  activities: StudioActivity[];
}> => {
  const groups: Array<{
    optionId: StudioOptionId;
    activities: StudioActivity[];
  }> = [];
  const groupsByOptionId = new Map<
    StudioOptionId,
    {
      optionId: StudioOptionId;
      activities: StudioActivity[];
    }
  >();

  activities.forEach((activity) => {
    const existingGroup = groupsByOptionId.get(activity.optionId);
    if (existingGroup) {
      existingGroup.activities.push(activity);
      return;
    }

    const nextGroup = {
      optionId: activity.optionId,
      activities: [activity],
    };
    groupsByOptionId.set(activity.optionId, nextGroup);
    groups.push(nextGroup);
  });

  return groups;
};

export const normalizeSelectionCount = (
  optionId: StudioOptionId,
  nextValue: number
): number => {
  const positiveValue = Math.max(0, nextValue);
  return isGameOption(optionId) ? (positiveValue > 0 ? 1 : 0) : positiveValue;
};
